import { useCallback, useEffect, useMemo, useState } from 'react'
import ApiEmailOutbox from '../../api/ApiEmailOutbox'
import ApiLeagues from '../../api/ApiLeagues'
import toastr from 'toastr'
import { getCurrentCycleKey } from './emailOutboxUtils'

/**
 * Centralized error handler for Email Outbox operations
 * @param {Error} err - The error object
 * @param {string} defaultMessage - Default error message to show
 */
function handleEmailOutboxError(err, defaultMessage) {
  console.error(defaultMessage, err)

  // Check for 403 Forbidden (environment restriction)
  if (err.response?.status === 403) {
    toastr.error(
      'Email sending is disabled in this environment. Only test sends are allowed.',
    )
    return
  }

  // Use error message from server or fallback to default
  toastr.error(err.message || defaultMessage)
}

function useEmailOutboxData({ isAdmin, leagueIdParam }) {
  const [leagues, setLeagues] = useState([])
  const [selectedLeagueId, setSelectedLeagueId] = useState(leagueIdParam || '')
  const [leagueName, setLeagueName] = useState('')
  const leagueId = selectedLeagueId

  const [drafts, setDrafts] = useState([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(null)

  const [statusFilter, setStatusFilter] = useState('')
  const [templateFilter, setTemplateFilter] = useState('')
  const [cycleFilter, setCycleFilter] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const [sortColumn, setSortColumn] = useState('generated_at')
  const [sortDirection, setSortDirection] = useState('desc')

  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 50

  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewDraft, setPreviewDraft] = useState(null)
  const [previewTab, setPreviewTab] = useState('html')

  const [showHelp, setShowHelp] = useState(false)

  const [showTestSendModal, setShowTestSendModal] = useState(false)
  const [testSendDraft, setTestSendDraft] = useState(null)
  const [testEmail, setTestEmail] = useState('')
  const [sendingTest, setSendingTest] = useState(false)

  const [sending, setSending] = useState(false)
  const [canceling, setCanceling] = useState(false)
  const [retrying, setRetrying] = useState(false)

  useEffect(() => {
    async function fetchLeagues() {
      try {
        const result = await ApiLeagues.getAll()
        setLeagues(result || [])
        if (!selectedLeagueId && result && result.length > 0) {
          setSelectedLeagueId(result[0].id.toString())
        }
      } catch (err) {
        handleEmailOutboxError(err, 'Failed to load leagues')
      }
    }

    if (isAdmin) {
      fetchLeagues()
    }
  }, [isAdmin, selectedLeagueId])

  useEffect(() => {
    async function fetchLeagueInfo() {
      try {
        const league = await ApiLeagues.getLeague(leagueId)
        setLeagueName(league.name || `League ${leagueId}`)
      } catch (err) {
        handleEmailOutboxError(err, 'Failed to load league information')
      }
    }

    if (isAdmin && leagueId) {
      fetchLeagueInfo()
    }
  }, [isAdmin, leagueId])

  const fetchDrafts = useCallback(async () => {
    if (!leagueId) return

    setLoading(true)
    try {
      const filters = {}
      if (statusFilter) filters.status = statusFilter
      if (templateFilter) filters.template_key = templateFilter
      if (cycleFilter) {
        filters.cycle_key =
          cycleFilter === 'current' ? getCurrentCycleKey() : cycleFilter
      }
      if (startDate) filters.start_date = startDate
      if (endDate) filters.end_date = endDate

      const result = await ApiEmailOutbox.getDrafts(leagueId, filters)
      setDrafts(result.drafts || [])
      setLastUpdated(new Date())
    } catch (err) {
      handleEmailOutboxError(err, 'Failed to load email drafts')
    } finally {
      setLoading(false)
    }
  }, [leagueId, statusFilter, templateFilter, cycleFilter, startDate, endDate])

  useEffect(() => {
    setCurrentPage(1)
    if (isAdmin) {
      fetchDrafts()
    }
  }, [isAdmin, fetchDrafts])

  const handleGenerateDrafts = async () => {
    setGenerating(true)
    try {
      const result = await ApiEmailOutbox.generateDrafts(leagueId)
      toastr.success(
        `Generated ${result.draftsCreated} drafts, ${result.duplicatesSkipped} duplicates skipped`,
      )
      fetchDrafts()
    } catch (err) {
      handleEmailOutboxError(err, 'Failed to generate drafts')
    } finally {
      setGenerating(false)
    }
  }

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const filteredDrafts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()

    if (!normalizedSearch) {
      return drafts
    }

    return drafts.filter((draft) => {
      const searchableFields = [
        draft.player_name,
        draft.user_email,
        draft.subject,
        draft.template_key,
        draft.cycle_key,
        draft.status,
      ]

      return searchableFields.some((field) =>
        String(field || '')
          .toLowerCase()
          .includes(normalizedSearch),
      )
    })
  }, [drafts, searchTerm])

  const sortedDrafts = useMemo(() => {
    const sorted = [...filteredDrafts].sort((a, b) => {
      let aVal = a[sortColumn]
      let bVal = b[sortColumn]

      if (aVal == null) aVal = ''
      if (bVal == null) bVal = ''

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase()
        bVal = bVal.toLowerCase()
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
      }

      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
    })

    return sorted
  }, [filteredDrafts, sortColumn, sortDirection])

  const totalDrafts = sortedDrafts.length
  const totalPages = Math.ceil(totalDrafts / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedDrafts = sortedDrafts.slice(startIndex, endIndex)

  const uniqueCycles = useMemo(() => {
    const cycles = new Set(drafts.map((d) => d.cycle_key).filter(Boolean))
    return Array.from(cycles).sort()
  }, [drafts])

  const draftCount = drafts.filter((d) => d.status === 'draft').length
  const failedCount = drafts.filter((d) => d.status === 'failed').length

  const handlePreview = (draft) => {
    setPreviewDraft(draft)
    setPreviewTab('html')
    setShowPreviewModal(true)
  }

  const handleTestSend = (draft) => {
    setTestSendDraft(draft)
    setTestEmail('')
    setShowTestSendModal(true)
  }

  const confirmTestSend = async () => {
    if (!testEmail || !testSendDraft) return

    setSendingTest(true)
    try {
      await ApiEmailOutbox.testSend(testSendDraft.id, testEmail)
      toastr.success(`Test email sent to ${testEmail}`)
      setShowTestSendModal(false)
      fetchDrafts()
    } catch (err) {
      handleEmailOutboxError(err, 'Failed to send test email')
    } finally {
      setSendingTest(false)
    }
  }

  const handleSend = async (draft) => {
    if (!window.confirm(`Send email to ${draft.user_email}?`)) return

    try {
      await ApiEmailOutbox.send(draft.id)
      toastr.success(`Email sent to ${draft.user_email}`)
      fetchDrafts()
    } catch (err) {
      handleEmailOutboxError(err, 'Failed to send email')
    }
  }

  const handleCancel = async (draft) => {
    if (!window.confirm(`Delete draft for ${draft.user_email}?`)) return

    try {
      await ApiEmailOutbox.cancel(draft.id)
      toastr.success('Draft deleted')
      fetchDrafts()
    } catch (err) {
      handleEmailOutboxError(err, 'Failed to delete draft')
    }
  }

  const handleRetry = async (draft) => {
    try {
      await ApiEmailOutbox.retry(draft.id)
      toastr.success('Draft reset to draft status')
      fetchDrafts()
    } catch (err) {
      handleEmailOutboxError(err, 'Failed to retry draft')
    }
  }

  const handleSendAllDrafts = async () => {
    if (
      !window.confirm(
        `Send ${draftCount} draft emails? This action cannot be undone.`,
      )
    )
      return

    setSending(true)
    try {
      const result = await ApiEmailOutbox.sendBatch(leagueId, {
        status: 'draft',
      })
      toastr.success(
        `Sent ${result.sent} emails, ${result.failed} failed, ${result.skipped} skipped`,
      )
      fetchDrafts()
    } catch (err) {
      handleEmailOutboxError(err, 'Failed to send batch')
    } finally {
      setSending(false)
    }
  }

  const handleCancelAllDrafts = async () => {
    if (
      !window.confirm(
        `Delete all ${draftCount} draft emails? This cannot be undone.`,
      )
    )
      return

    setCanceling(true)
    try {
      const cancelPromises = drafts
        .filter((d) => d.status === 'draft' || d.status === 'canceled')
        .map((d) => ApiEmailOutbox.cancel(d.id))

      const results = await Promise.allSettled(cancelPromises)
      const succeeded = results.filter((r) => r.status === 'fulfilled').length
      const failed = results.filter((r) => r.status === 'rejected').length

      if (failed > 0) {
        toastr.warning(`Deleted ${succeeded} drafts, ${failed} failed`)
      } else {
        toastr.success(`Deleted ${succeeded} drafts`)
      }
      fetchDrafts()
    } catch (err) {
      handleEmailOutboxError(err, 'Failed to delete all drafts')
    } finally {
      setCanceling(false)
    }
  }

  const handleRetryFailed = async () => {
    if (!window.confirm(`Retry all ${failedCount} failed drafts?`)) return

    setRetrying(true)
    try {
      const retryPromises = drafts
        .filter((d) => d.status === 'failed')
        .map((d) => ApiEmailOutbox.retry(d.id))

      const results = await Promise.allSettled(retryPromises)
      const succeeded = results.filter((r) => r.status === 'fulfilled').length
      const failed = results.filter((r) => r.status === 'rejected').length

      if (failed > 0) {
        toastr.warning(
          `Reset ${succeeded} failed drafts, ${failed} still failed`,
        )
      } else {
        toastr.success(`Reset ${succeeded} failed drafts to draft status`)
      }
      fetchDrafts()
    } catch (err) {
      handleEmailOutboxError(err, 'Failed to retry failed drafts')
    } finally {
      setRetrying(false)
    }
  }

  return {
    leagueId,
    leagues,
    selectedLeagueId,
    setSelectedLeagueId,
    leagueName,
    drafts,
    loading,
    generating,
    lastUpdated,
    statusFilter,
    setStatusFilter,
    templateFilter,
    setTemplateFilter,
    cycleFilter,
    setCycleFilter,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    searchTerm,
    setSearchTerm,
    sortColumn,
    sortDirection,
    handleSort,
    currentPage,
    setCurrentPage,
    totalDrafts,
    totalPages,
    startIndex,
    endIndex,
    paginatedDrafts,
    uniqueCycles,
    draftCount,
    failedCount,
    showPreviewModal,
    setShowPreviewModal,
    previewDraft,
    previewTab,
    setPreviewTab,
    showHelp,
    setShowHelp,
    showTestSendModal,
    setShowTestSendModal,
    testSendDraft,
    testEmail,
    setTestEmail,
    sendingTest,
    sending,
    canceling,
    retrying,
    handleGenerateDrafts,
    handleRefresh: fetchDrafts,
    handlePreview,
    handleTestSend,
    confirmTestSend,
    handleSend,
    handleCancel,
    handleRetry,
    handleSendAllDrafts,
    handleCancelAllDrafts,
    handleRetryFailed,
  }
}

export default useEmailOutboxData
